import * as React from "react";
import { AlertTriangle, Battery, Bell } from "lucide-react";
import { ImageLoader } from "../../../ImageLoader";
import { ListViewCard } from "../ListViewCard";
import { DeviceHealthIndicator } from "../../../DeviceHealthIndicator";
import { OrgThumbnail } from "../../../OrgThumbnail";
import { resolveLastSignalAt } from "../../../DeviceHealthIndicator/resolveLastSignalAt";
import { shortDate } from "../DeckCard/logic/formatters";
import type { ListViewCardAction, ListViewCardLayout, ListViewCTAGroup } from "../ListViewCard";
import * as alertBadgeStyles from "./ListViewProductCard.alerts.css";

const API_BASE = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL ?? "http://localhost:3003/api";

const SEVERITY_COLORS: Record<number, string> = {
  1: "#6B7280",
  2: "#1D4ED8",
  3: "#B45309",
  4: "#C2410C",
  5: "#B91C1C",
};

function highestSeverityAlert(
  alerts: Array<{ code: string; name?: string | null; type?: string | null; severity?: number | null }>,
) {
  if (alerts.length === 0) return null;
  return alerts.reduce((best, a) => {
    const sev = Math.min(5, Math.max(1, a.severity ?? 1));
    const bestSev = Math.min(5, Math.max(1, best.severity ?? 1));
    return sev >= bestSev ? a : best;
  });
}

export interface ListViewProductCardProduct {
  id: string;
  name: string;
  status: string;
  serial?: string | null;
  assetTag?: string | null;
  isSimulated?: boolean;
  isSimulating?: boolean;
  simulationExpAt?: string | null;
  simulationStartedAt?: string | null;
  productModel?: { name: string } | null;
  organization?: { name: string; logoUrl?: string | null } | null;
  mainImageUrl?: string | null;
  createdAt?: string;
  /** ISO timestamp of last device telemetry signal. */
  lastTelemetryAt?: string | null;
  /** ISO timestamp of last device shadow update. */
  lastShadowAt?: string | null;
  /** Max of lastTelemetryAt and lastShadowAt. */
  lastSignalAt?: string | null;
}

export interface ListViewProductCardProps {
  layout: ListViewCardLayout;
  product: ListViewProductCardProduct;
  accessToken?: string | null;
  contextActions?: ListViewCardAction[];
  onClick?: () => void;
  onOrganizationClick?: () => void;
  ctaGroups?: ListViewCTAGroup[];
  animatedHighlight?: boolean;
  /** Increment when the row receives a real-time update to re-trigger image fetch. */
  refreshKey?: number;
  /** ISO timestamp of the most recent device telemetry signal for this product. */
  lastTelemetryAt?: string | null;
  /** ISO timestamp of the most recent device shadow update for this product. */
  lastShadowAt?: string | null;
  /** Unix ms timestamp of the product-service start, for initializing state. */
  serviceStartedAt?: number;
  /** Active alerts for throbbing badge (after SIM). */
  activeAlerts?: Array<{
    code: string;
    name?: string | null;
    type?: string | null;
    severity?: number | null;
  }>;
}

export function ListViewProductCard({
  layout,
  product,
  accessToken,
  contextActions,
  onClick,
  onOrganizationClick,
  ctaGroups,
  animatedHighlight,
  refreshKey,
  lastTelemetryAt,
  lastShadowAt,
  serviceStartedAt,
  activeAlerts = [],
}: ListViewProductCardProps) {
  const [imgUrls, setImgUrls] = React.useState<{ thumb: string; card: string } | null>(null);

  React.useEffect(() => {
    if (!accessToken) {
      setImgUrls(null);
      return;
    }

    let cancelled = false;

    async function load() {
      let thumb: string | null = null;
      let card: string | null = product.mainImageUrl ?? null;

      if (!card) {
        try {
          const res = await fetch(`${API_BASE}/products/${product.id}/images`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          if (res.ok) {
            const data = (await res.json()) as { images?: Array<{ url: string; thumbUrl?: string | null; cardUrl?: string | null; sortOrder: number }> };
            const sorted = (data.images ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder);
            const first = sorted[0];
            if (first) {
              thumb = first.thumbUrl ?? first.url;
              card = first.cardUrl ?? first.url;
            }
          }
        } catch {
          // gallery unavailable
        }
      }

      if (!cancelled && card) {
        setImgUrls({ thumb: thumb ?? card, card });
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [product.id, product.mainImageUrl, accessToken, refreshKey]);

  const image = imgUrls ? (
    <ImageLoader src={imgUrls.card} placeholderSrc={imgUrls.thumb} />
  ) : (
    <Battery size={layout === "deck" ? 40 : 32} style={{ opacity: 0.55 }} />
  );

  const tags = [
    product.serial && `S/N: ${product.serial}`,
    product.assetTag && `#${product.assetTag}`,
  ]
    .filter(Boolean)
    .join(" · ");

  const subtitle = [product.productModel?.name, tags].filter(Boolean).join(" · ") || undefined;

  const statusKey = (product.status ?? "").toLowerCase();
  const statusVariantMap: Record<string, "active" | "disabled" | "pending" | "default"> = {
    inactive: "disabled",
    decommissioned: "disabled",
  };

  const badges = [
    // Only show status badge for non-active statuses
    ...(statusKey === 'inactive' || statusKey === 'decommissioned'
      ? [{ id: "status", label: statusKey.charAt(0).toUpperCase() + statusKey.slice(1), variant: statusVariantMap[statusKey] ?? ("default" as const) }]
      : []),
    // Simulated products get a plain SIM badge
    ...(product.isSimulated
      ? [{ id: "simulated", label: "SIM", variant: "pending" as const }]
      : []),
    ...(activeAlerts.length > 0
      ? (() => {
          const top = highestSeverityAlert(activeAlerts);
          const sev = Math.min(5, Math.max(1, top?.severity ?? 3));
          const color = SEVERITY_COLORS[sev] ?? SEVERITY_COLORS[3];
          const Icon = top?.type === "fault" ? AlertTriangle : Bell;
          return [{
            id: "active-alerts",
            label: activeAlerts.length === 1 ? "1 active alert" : `${activeAlerts.length} active alerts`,
            title: activeAlerts
              .map((a) => {
                const name = (a.name ?? "").trim();
                return name && name !== a.code ? `${a.code} — ${name}` : a.code;
              })
              .join("\n"),
            color,
            icon: (
              <span className={alertBadgeStyles.iconWrap} aria-hidden>
                <span className={alertBadgeStyles.pulseRing} />
                <Icon size={12} strokeWidth={2.25} className={alertBadgeStyles.iconGlyph} />
              </span>
            ),
          }];
        })()
      : []),
  ];

  const footerText = product.createdAt ? `Added ${shortDate(product.createdAt)}` : undefined;

  return (
    <ListViewCard
      id={product.id}
      layout={layout}
      imageFormat="cover"
      image={image}
      title={product.name}
      subtitle={subtitle}
      badgeRowLeading={(() => {
        const telAt = lastTelemetryAt ?? product.lastTelemetryAt ?? null;
        const shadAt = lastShadowAt ?? product.lastShadowAt ?? null;
        const sigAt = resolveLastSignalAt(product.lastSignalAt, telAt, shadAt);
        return (
          <>
          {product.organization && (
            <OrgThumbnail
              name={product.organization.name}
              logoUrl={product.organization.logoUrl}
              size={20}
              onClick={onOrganizationClick}
            />
          )}
          <DeviceHealthIndicator
            lastSignalAt={sigAt}
            lastTelemetryAt={telAt}
            lastShadowAt={shadAt}
            productCreatedAt={product.createdAt}
            serviceStartedAt={serviceStartedAt}
            isSimulated={product.isSimulated}
            isSimulating={product.isSimulating}
            simulationStartedAt={product.simulationStartedAt}
            simulationExpAt={product.simulationExpAt}
            size="sm"
          />
          </>
        );
      })()}
      badges={badges}
      footerText={footerText}
      onClick={onClick}
      contextActions={contextActions}
      ctaGroups={ctaGroups}
      animatedHighlight={animatedHighlight}
    />
  );
}
